/**
 * Shared "Do Not Sell or Share" resolution for every app that loads Segment (xcc-client checkout and
 * core-mkt marketing). Pure functions only: callers pass in `document.cookie` and `window.navigator`,
 * so nothing here touches a browser global and everything is safe to import during SSR.
 *
 * A visitor is opted out when the consent cookie holds anything but `'0'` OR the browser sends Global
 * Privacy Control.
 * GPC wins over a stored `'0'`: core-mkt historically wrote `'0'` for every visitor without a cookie,
 * so a `'0'` is not proof of an affirmative opt-in, and CCPA regs §7025(c)(3) require the signal to
 * be honored until the consumer affirmatively consents.
 */

/** Cookie holding the visitor's choice: `'1'` = do not sell/share, `'0'` = allow. */
export const CONSENT_COOKIE_NAME = 'consent-ad-personalization';

/** Cookie value meaning "do not sell or share". */
export const CONSENT_OPT_OUT_VALUE = '1';

/** Cookie value meaning "allow" — an explicit privacy-manager choice or core-mkt's default write. */
export const CONSENT_ALLOW_VALUE = '0';

/** Lifetime of the consent cookie (~10 years), matching core-mkt's PrivacyCheckService. */
export const CONSENT_COOKIE_EXPIRY_DAYS = 3700;

/**
 * Segment destinations that sell or share data for cross-context advertising, keyed by the exact
 * destination name Segment matches on (case- and punctuation-sensitive; see each source's
 * `cdn.segment.com/v1/projects/<writeKey>/settings` → `integrations`).
 *
 * Passed as `integrations` both to `analytics.load()` (device-mode destinations are never
 * instantiated, so their scripts never load) and to every page/track/identify call (cloud-mode
 * delivery is suppressed). Google Tag Manager is listed because the brand containers fire ad pixels
 * (OpenAI ads conversion, MNTN). Unlisted destinations (Segment.io, Optimizely, Iterable, Google
 * Analytics) keep receiving events.
 */
export const AD_OPT_OUT_INTEGRATIONS: Readonly<Record<string, false>> = Object.freeze({
  'Facebook Pixel': false,
  'Facebook Conversions API (Actions)': false,
  'Google AdWords New': false,
  'Bing Ads': false,
  'Tiktok Conversions': false,
  'LinkedIn Insight Tag': false,
  'Google Tag Manager': false,
});

/** Segment consent context attached to events so workspace-side consent mapping can act on it. */
export interface AdConsentContext {
  consent: {
    categoryPreferences: {
      DataSharing: boolean;
    };
  };
}

const CONSENT_COOKIE_PATTERN = new RegExp(`(?:^|;\\s*)${CONSENT_COOKIE_NAME}=([^;]*)`);

/** Reads the consent cookie's value out of a `document.cookie` string. */
export function readConsentCookie(cookieString: string | null | undefined): string | null {
  if (!cookieString) {
    return null;
  }
  const match = cookieString.match(CONSENT_COOKIE_PATTERN);
  return match ? match[1] : null;
}

/**
 * True when the browser sends Global Privacy Control. Takes `unknown` so callers can pass a
 * possibly-missing `window.navigator`; a throwing getter reads as no signal.
 */
export function hasGlobalPrivacyControl(nav: unknown): boolean {
  try {
    return !!(nav && (nav as { globalPrivacyControl?: unknown }).globalPrivacyControl);
  } catch {
    return false;
  }
}

/**
 * True when ad tracking must be suppressed: GPC on, or a stored choice other than allow (`'0'`).
 * Fails closed on an unrecognized cookie value, matching the `DataSharing` rule the apps already
 * applied (`'0'` or no cookie → sharing allowed, anything else → not).
 */
export function isAdOptOut(cookieString: string | null | undefined, nav: unknown): boolean {
  return resolveAdOptOut(readConsentCookie(cookieString), hasGlobalPrivacyControl(nav));
}

/**
 * {@link isAdOptOut} for callers that already hold the parsed cookie value and GPC flag (core-mkt
 * reads both through its own SSR-aware services).
 */
export function resolveAdOptOut(cookieValue: string | null | undefined, globalPrivacyControl: boolean): boolean {
  return (cookieValue != null && cookieValue !== CONSENT_ALLOW_VALUE) || globalPrivacyControl;
}

/**
 * True when GPC is on and the stored choice is not already the opt-out, i.e. the caller should
 * persist `'1'` so cookie-only readers (servers, other subdomains, the privacy manager) see the
 * opt-out. This includes overwriting a stored `'0'`, which GPC overrides anyway.
 */
export function shouldPersistGpcOptOut(cookieString: string | null | undefined, nav: unknown): boolean {
  return hasGlobalPrivacyControl(nav) && readConsentCookie(cookieString) !== CONSENT_OPT_OUT_VALUE;
}

/** Minimal shape of a Segment source-middleware call (`analytics.addSourceMiddleware`). */
export interface AdOptOutMiddlewareParams {
  payload: { obj: { integrations?: Record<string, unknown>; context?: Record<string, unknown> } };
  next: (payload: AdOptOutMiddlewareParams['payload']) => void;
}

/**
 * Segment source middleware that re-checks the opt-out on every event and, when opted out, switches
 * the ad destinations off and stamps `DataSharing: false`. Registered on every Analytics instance so
 * events that bypass the app's wrappers (Signals/Event Builder, third-party `window.analytics`
 * calls) and opt-outs made mid-session are enforced too.
 */
export function createAdOptOutMiddleware(isOptedOut: () => boolean): (params: AdOptOutMiddlewareParams) => void {
  return ({ payload, next }) => {
    if (isOptedOut()) {
      payload.obj.integrations = { ...payload.obj.integrations, ...AD_OPT_OUT_INTEGRATIONS };
      payload.obj.context = { ...payload.obj.context, ...buildAdConsentContext(true) };
    }
    next(payload);
  };
}

/** Builds the Segment consent context for an event. */
export function buildAdConsentContext(optedOut: boolean): AdConsentContext {
  return { consent: { categoryPreferences: { DataSharing: !optedOut } } };
}

/**
 * Domain for the consent cookie: the last two hostname labels (`checkout.aceable.com` →
 * `aceable.com`), matching core-mkt's PrivacyCheckService on real hostnames so both apps write one
 * shared cookie rather than two that shadow each other. Unlike PrivacyCheckService, returns null
 * for single-label hosts (localhost) and IPv4 addresses, where a host-only cookie is the only kind
 * the browser accepts (local dev only).
 */
export function consentCookieDomain(hostname: string | null | undefined): string | null {
  if (!hostname || !hostname.includes('.') || /^\d+(\.\d+){3}$/.test(hostname)) {
    return null;
  }
  return hostname.split('.').slice(-2).join('.');
}

/** Builds the `document.cookie` assignment that persists the opt-out for {@link hostname}. */
export function buildOptOutCookie(hostname: string | null | undefined, now: Date = new Date()): string {
  const expires = new Date(now.getTime() + CONSENT_COOKIE_EXPIRY_DAYS * 24 * 60 * 60 * 1000).toUTCString();
  const domain = consentCookieDomain(hostname);
  return `${CONSENT_COOKIE_NAME}=${CONSENT_OPT_OUT_VALUE}; expires=${expires}; path=/${
    domain ? `; domain=${domain}` : ''
  }`;
}
